import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageService, SharedModule } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { RepresentativeFormComponent } from '@app/areas/admin/representatives/forms/representative-form/representative-form.component';
import { RepresentativeFormService } from '@app/areas/admin/representatives/services/representative-form.service';
import { RepresentativesApiService } from '@app/areas/admin/representatives/services/representatives.api.service';

@Component({
  selector: 'app-new-representative',
  templateUrl: './new-representative.component.html',
  styleUrls: ['./new-representative.component.scss'],
  standalone: true,
  imports: [RepresentativeFormComponent, ButtonModule, CardModule, SharedModule, AsyncPipe, NgIf],
})
export class NewRepresentativeComponent implements OnInit, OnDestroy {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly representativeFormService = inject(RepresentativeFormService);
  private readonly representativesApiService = inject(RepresentativesApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected isSaveDisabled$ = this.representativeFormService.getForm().statusChanges.pipe(
    startWith(this.representativeFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  protected isSaving = false;
  private subs = new Subscription();

  ngOnInit(): void {
    this.representativeFormService.initializeForAdd();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected goBackToRepresentativesTable(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  protected saveRepresentative(): void {
    const form = this.representativeFormService.getForm();
    if (!form.valid) {
      return;
    }

    this.isSaving = true;
    const coalitionId = this.applicationProfileService.getApplicationProfile().Representative.CoalitionId;
    const formValue = form.getRawValue();

    this.subs.add(
      this.representativesApiService
        .create({
          CoalitionId: coalitionId,
          NameFirst: formValue.NameFirst!,
          NameLast: formValue.NameLast!,
          EmailAddress: formValue.EmailAddress!,
          PhoneNumber: formValue.PhoneNumber?.replace(/\+/g, '') ?? undefined,
          AccessType: formValue.AccessType ?? undefined,
          RepresentativeType: formValue.RepresentativeType ?? undefined,
          LanguageType: formValue.LanguageType ?? undefined,
        })
        .subscribe((representative) => {
          this.isSaving = false;
          if (representative) {
            this.router.navigate(['../', representative.RepresentativeId], { relativeTo: this.route });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: New Representative',
              detail: 'Something went wrong while creating the representative',
            });
          }
        })
    );
  }
}
